import React, { useEffect, useState } from 'react';
import API from '../api';
import { useParams } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, setHours, setMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CheckoutPage from './CheckoutPage';
import './calendar.css';

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const AllBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState('month'); // default is Month view
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [workingDays, setWorkingDays] = useState([]);
  const [workingHours, setWorkingHours] = useState({});
  const [disabledDates, setDisabledDates] = useState([]);
  const [disabledDateInput, setDisabledDateInput] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');

  const [isCheckoutMode, setIsCheckoutMode] = useState(false);
  const [checkoutPayload, setCheckoutPayload] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  const { businessId: urlBusinessId } = useParams();
  const localUser = JSON.parse(localStorage.getItem('user'));
  const businessId = localUser?.user?.id || localUser?.id || urlBusinessId;
  const empId = localUser?.user?.id || localUser?.id || urlBusinessId;
  const role = localStorage.getItem('role');
  const timeZone = 'America/New_York'; // keep calendar times in EST/EDT

  /** Fetch bookings */
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const id = localStorage.getItem('id');
        const url =
          role === 'admin'
            ? `/auth/getAllBookings?businessId=${id}`
            : `/auth/getAllBookings?employeeId=${empId}`;

        const { data } = await API.get(url);

        const parsed = await Promise.all(
          data.map(async (booking) => {
            try {
              const contactRes = await API.get(`/auth/getContactById?contactId=${booking.contact}`);
              const contactName = contactRes.data?.name || 'Unknown Contact';
              return {
                id: booking._id,
                serviceId: booking.serviceId,
                employeeId: booking.employeeId,
                contactId: booking.contact,
                contactName,
                businessId: booking.business_id,
                date: new Date(booking.bookingDateTime),
                createdAt: new Date(booking.created_at),
                status: booking.status || 'pending',
              };
            } catch {
              return {
                id: booking._id,
                serviceId: booking.serviceId,
                employeeId: booking.employeeId,
                contactId: booking.contact,
                contactName: 'Unknown Contact',
                businessId: booking.business_id,
                date: new Date(booking.bookingDateTime),
                createdAt: new Date(booking.created_at),
                status: booking.status || 'pending',
              };
            }
          })
        );

        setBookings(parsed);
      } catch (err) {
        console.error('❌ Error fetching bookings:', err);
        setError('Failed to fetch bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [empId, role]);

  /** Business / employee setup data */
  useEffect(() => {
    const fetchSetupData = async () => {
      try {
        const businessRes = await API.get(`/auth/business-detail?businessId=${businessId}`);
        const businessDisabled = businessRes.data.disabledDates || [];
        setWorkingDays(businessRes.data.workingDays || []);
        setWorkingHours(businessRes.data.workingHours || {});
        setDisabledDates(businessDisabled);

        const employeesRes = await API.get(`/auth/employees/business/${businessId}`);
        setEmployees(employeesRes.data);

        if (role === 'employee' && empId) {
          const empRes = await API.get(`/auth/employees/?id=${empId}`);
          const empDisabled = empRes.data.disabledDates || [];
          setDisabledDates([...new Set([...businessDisabled, ...empDisabled])]);
        }
      } catch (err) {
        console.error('Failed to fetch business or employee details', err);
      }
    };

    fetchSetupData();
  }, [businessId, role, empId]);

  const handleDisableDate = async () => {
    if (!disabledDateInput) return;
    try {
      if (selectedEmployeeId === 'all') {
        // const updatedDates = [...new Set([...disabledDates, disabledDateInput])];
        // await API.put(`/auth/business-detail`, {
        //   businessId,
        //   disabledDates: updatedDates,
        // });
        // setDisabledDates(updatedDates);


       
      // Collect all employee IDs
      const allEmployeeIds = employees.map(emp => emp._id);

      await API.put(`/auth/updateEmployee`, {
        employeeIds: allEmployeeIds,  // <-- send all employees
        disabledDate: disabledDateInput,
      });

      // Update UI instantly
      setDisabledDates(prev => [...new Set([...prev, disabledDateInput])]);

    
      } else {
        await API.put(`/auth/updateEmployee`, {
          employeeId: selectedEmployeeId,
          disabledDates: [disabledDateInput],
        });
      }
      setDisabledDateInput('');
    } catch (err) {
      console.error('❌ Failed to update disabled dates:', err);
    }
  };

  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    setShowModal(true);
    try {
      const [contactRes, serviceRes, employeeRes] = await Promise.all([
        API.get(`/auth/getContactById?contactId=${event.contactId}`),
        API.get(`/auth/getServiceById?serviceId=${event.serviceId}`),
        API.get(`/auth/employees/${event.employeeId}`),
      ]);

      setContactDetails(contactRes.data);
      setServiceDetails(serviceRes.data);
      setEmployeeDetails(employeeRes.data);
    } catch (err) {
      console.error('Error fetching contact/service details:', err);
    }
  };

  const handleCheckout = () => {
    if (!selectedEvent || !contactDetails || !serviceDetails) {
      alert('Missing booking details.');
      return;
    }

    setCheckoutPayload({
      bookingId: selectedEvent.id,
      contact: contactDetails,
      selectedService: serviceDetails,
    });

    setIsCheckoutMode(true);
    setShowModal(false);
  };

  const handleNoShow = () => {
    if (!selectedEvent) {
      alert('Missing booking details.');
      return;
    }
    API.put(`/auth/updateBookingStatus`, {
      bookingId: selectedEvent.id,
      status: 'no-show',
    })
      .then(() => {
        setBookings((prev) =>
          prev.map((b) => (b.id === selectedEvent.id ? { ...b, status: 'no-show' } : b))
        );
        setShowModal(false);
      })
      .catch((err) => {
        console.error('❌ Failed to update booking status:', err);
        alert('Failed to mark booking as no-show.');
      });
  };

  /** Per-day open/close times for week/day views */
  const getTimeBoundsForDay = (date) => {
    const dayName = format(date, 'EEEE');
    const hours = workingHours?.[dayName];
    if (hours?.opensAt && hours?.closesAt) {
      const [startH, startM] = hours.opensAt.split(':').map(Number);
      const [endH, endM] = hours.closesAt.split(':').map(Number);
      const baseDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return {
        min: setHours(setMinutes(new Date(baseDate), startM), startH),
        max: setHours(setMinutes(new Date(baseDate), endM), endH),
      };
    }
    return {
      min: setHours(setMinutes(new Date(date), 0), 0),
      max: setHours(setMinutes(new Date(date), 59), 23),
    };
  };

  /** Build react-big-calendar events */
  const events = bookings.map((booking) => {
    const startDate = toZonedTime(booking.date, timeZone);
    return {
      title: booking.contactName, // show contact name as the event title
      start: startDate,
      end: new Date(startDate.getTime() + 15 * 60 * 1000),
      allDay: false,
      contactId: booking.contactId,
      serviceId: booking.serviceId,
      status: booking.status,
      id: booking.id,
      employeeId: booking.employeeId,
    };
  });

  // Auto-switch to DAY view on mobile
useEffect(() => {
  const handleResize = () => {
    if (window.innerWidth < 768) {
      setCurrentView("day");  // Force Day view
    }
  };
  handleResize(); // Run once on load
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);


  if (loading) return <p>Loading bookings...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return isCheckoutMode ? (
    <CheckoutPage
      payload={checkoutPayload}
      onBack={() => {
        setIsCheckoutMode(false);
        setCheckoutPayload(null);
      }}
    />
  ) : (
    <div className="all-bookings-page">{/* PARENT CLASS — all styles are scoped under this */}
      {/* Header */}
      <header className="all-bookings-header">
        <h1 className="all-bookings-title">All Booking</h1>
        <p className="all-bookings-sub">Disable Specific Booking Dates</p>
      </header>

      {/* Controls card */}
      <section className="controls-card">
        <div className="controls-row">
          <div className="ctrl-field">
            <label className="ctrl-label">Apply To</label>
            <select
              className="ctrl-select"
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
            >
              <option value="all">ALL Employees</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ctrl-field">
            <label className="ctrl-label">Date</label>
            <input
              type="date"
              className="ctrl-input"
              value={disabledDateInput}
              onChange={(e) => setDisabledDateInput(e.target.value)}
            />
          </div>

          <button className="ctrl-btn" onClick={handleDisableDate}>
            Disable Date
          </button>
        </div>
      </section>

      {/* Calendar card */}
      <section className="calendar-card">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          timeslots={2}
          defaultView="month"          // month by default
          view={currentView}
          onView={setCurrentView}
          views={['month', 'week', 'day']}
          toolbar
          step={15}
          messages={{ time: 'Time (EST)' }}
          getNow={() => new Date()}
          min={new Date(1970, 1, 1, 0, 0)}
          max={new Date(1970, 1, 1, 23, 59)}
          onSelectEvent={handleSelectEvent}
          dayPropGetter={(date) => {
            const dayName = format(date, 'EEEE');
            const dateStr = format(date, 'yyyy-MM-dd');
            const isDisabled = disabledDates.includes(dateStr);
            const isNotWorking = workingDays.length > 0 && !workingDays.includes(dayName);

            if (isDisabled || isNotWorking) {
              return {
                className: 'rbc-day-disabled',
                style: { backgroundColor: 'rgba(255, 0, 0, 0.1)' },
              };
            }
            return {};
          }}
          slotPropGetter={(date) => {
            const { min, max } = getTimeBoundsForDay(date);
            if (date < min || date >= max) {
              return {
                style: {
                  backgroundColor: 'rgba(255, 220, 40, .15)',
                  color: '#ccc',
                },
              };
            }
            return {};
          }}
          // Color-code event status; title-only (time hidden via CSS)
          eventPropGetter={(event) => {
            let backgroundColor = '#3B47CE';
            if (event.status === 'no-show') backgroundColor = '#EF4444';
            else if (event.status === 'Billed') backgroundColor = '#22C55E';

            return {
              style: {
                backgroundColor,
                color: 'white',
                borderRadius: '10px',
                padding: '2px 6px',
                border: 'none',
              },
            };
          }}
          style={{ height: 'auto' }} // height handled by CSS
        />
      </section>

      {/* Modal */}
      {showModal && selectedEvent && (
  <div
    className="booking-modal-overlay"
    onClick={(e) => {
      if (e.target.classList.contains('booking-modal-overlay')) {
        setShowModal(false); // Close when clicking outside
      }
    }}
  >
    <div className="booking-modal">
      <h3>Booking Details</h3>

      <section>
        <div className="detail-card">
          <h4>Booking</h4>
          <p><strong>Start:</strong> {format(selectedEvent.start, 'do MMMM h:mma')}</p>
          <p><strong>End:</strong> {format(selectedEvent.end, 'do MMMM h:mma')}</p>
        </div>

        {contactDetails && (
          <div className="detail-card">
            <h4>Contact</h4>
            <p><strong>Name:</strong> {contactDetails.name}</p>
            <p><strong>Email:</strong> {contactDetails.email}</p>
          </div>
        )}

        {serviceDetails && (
          <div className="detail-card">
            <h4>Service</h4>
            <p><strong>Name:</strong> {serviceDetails.name}</p>
            <p><strong>Price:</strong> ${serviceDetails.price}</p>
          </div>
        )}

        {employeeDetails && (
          <div className="detail-card">
            <h4>Booking For</h4>
            <p><strong>Employee:</strong> {employeeDetails.name}</p>
            <p><strong>Status:</strong> {selectedEvent.status}</p>
          </div>
        )}
      </section>

      <div className="actions">
        <button
          className="confirm-btn"
          onClick={handleCheckout}
          style={{
            display:
              selectedEvent.status === 'no-show' || selectedEvent.status === 'Billed'
                ? 'none'
                : 'inline-flex',
          }}
        >
          Confirm
        </button>

        <button
          className="noshow-btn"
          onClick={handleNoShow}
          style={{
            display:
              selectedEvent.status === 'no-show' || selectedEvent.status === 'Billed'
                ? 'none'
                : 'inline-flex',
          }}
        >
          No Show
        </button>

        <button className="close-btn" onClick={() => setShowModal(false)}>
          Close
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

const modalStyle = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    padding: 20,
    borderRadius: 8,
    width: 600,
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
  },
};

export default AllBookingsPage;
